/* Courier Setup domain helpers + enum⇄label maps (no endpoints here — those live
   in constants.js / supplyApiSlice). Split out of the old api.js so screens can
   import response/error helpers and label maps without a bespoke service layer. */

/* GenericAPIResponse envelope → payload. Search endpoints return ResultData {data,count}. */
export const unwrap = (res) => res?.data?.responseObj ?? res?.data?.data ?? res?.data

/* Pull the most specific backend message. Handles the raw axios error (e.response.data)
   AND the RTK Query error shape (e.data), and the common envelope fields — incl. the
   plain `message` the upload endpoints return (e.g. "Allowed file types: [...]"). */
export const apiError = (e) => {
  const d = e?.response?.data || e?.data || {}
  return (
    d?.exception?.message ||
    d?.errorMsg ||
    d?.message ||
    /* PCIS-shaped envelopes carry the reason on meta.message ({meta: {...}, result: null}), so
       without this a 400 surfaced only as axios's "Request failed with status code 400" and the
       actual explanation — e.g. the routing split's 92-day window cap — was discarded. */
    d?.meta?.message ||
    (typeof d?.exception === 'string' ? d.exception : undefined) ||
    e?.message ||
    'Something went wrong.'
  )
}

/* ── Backend enum ⇄ UI label maps ─────────────────────────────────────── */
export const POC_ROLE_ENUM = {
  'Key Account Manager (KAM)': 'KAM',
  'Account Head': 'ACCOUNT_HEAD',
  'Business Head / VP': 'BUSINESS_HEAD',
  'Weight Discrepancy POC': 'WEIGHT_DISCREPANCY_POC',
  'COD Remittance POC': 'COD_REMITTANCE_POC',
  'Performance Escalation POC': 'PERFORMANCE_ESCALATION_POC',
  'Technical Integration POC': 'TECHNICAL_INTEGRATION_POC',
  'Finance / Billing POC': 'FINANCE_BILLING_POC',
}
export const POC_ROLE_LABEL = Object.fromEntries(Object.entries(POC_ROLE_ENUM).map(([l, e]) => [e, l]))

export const INTEGRATION_ENUM = {
  'Clickpost (aggregator)': 'CLICKPOST',
  'Direct API': 'DIRECT_API',
  'Manual (no integration)': 'MANUAL',
}
export const INTEGRATION_LABEL = Object.fromEntries(Object.entries(INTEGRATION_ENUM).map(([l, e]) => [e, l]))

export const AGREEMENT_TYPE_ENUM = {
  'Master Agreement (MSA)': 'MSA',
  'Commercial Addendum': 'COMMERCIAL_ADDENDUM',
  'Operational Addendum': 'OPERATIONAL_ADDENDUM',
}
export const AGREEMENT_TYPE_LABEL = Object.fromEntries(Object.entries(AGREEMENT_TYPE_ENUM).map(([l, e]) => [e, l]))

/* stage: backend enum string ⇄ UI numeric stage */
export const STAGE_ENUM = {1: 'EMAIL_RECEIVED', 2: 'SIGNED'}
export const STAGE_NUM = {EMAIL_RECEIVED: 1, SIGNED: 2}

/* ── CP Zone Mapping defensive normalizers (camelCase DTO OR snake_case Mongo) ── */
const zPick = (o, ...ks) => {
  for (let i = 0; i < ks.length; i++) if (o && o[ks[i]] != null) return o[ks[i]]
  return undefined
}
const zNum = (v) => (v == null || isNaN(+v) ? undefined : +v)

/** One override doc → flat row (mode upper-cased, pin/city/state). */
export const normOverride = (o) => ({
  id: zPick(o, 'overrideId', 'override_id', 'id'),
  pickup: zPick(o, 'pickupPincode', 'pickup_pincode') || '',
  mode: String(zPick(o, 'mode') || '').toUpperCase(),
  drops: zPick(o, 'drops') || [],
  zone: zNum(zPick(o, 'zone')),
  deleted: zPick(o, 'deleted') === true,
})

/** ZoneSetupView → the shape the screen consumes. */
export const normView = (raw) => {
  const v = raw || {}
  const setup = zPick(v, 'setup') || {}
  const base = zPick(v, 'parentBase', 'base', 'zoneBase', 'zone_base') || {}
  const overridesRaw = zPick(setup, 'overrides') || zPick(v, 'overrides') || []
  const liveIds = zPick(v, 'liveOverrideIds', 'live_override_ids')
  return {
    code: zPick(v, 'accountCode', 'code', 'account_code', 'cpAccountCode', 'cp_account_code'),
    prozoId: zPick(v, 'prozoId', 'prozo_id'),
    parentId: zPick(v, 'parentId', 'parent_id'),
    parentName: zPick(v, 'parentName', 'parent', 'parent_name'),
    displayName: zPick(v, 'displayName', 'display_name', 'name'),
    base: {
      // backend may nest the base (parentBase/zoneBase) OR send flat parentBase* fields
      status: String(zPick(base, 'status') || zPick(v, 'parentBaseStatus', 'parent_base_status') || 'NONE').toUpperCase(),
      fileCount:
        zNum(zPick(base, 'fileCount', 'file_count')) ||
        zNum(zPick(v, 'parentBaseFileCount', 'parent_base_file_count')) ||
        0,
      seededFrom:
        zPick(base, 'seededFromProzoId', 'seeded_from_prozo_id') ||
        zPick(v, 'parentBaseSeededFromProzoId', 'parent_base_seeded_from_prozo_id'),
      seededAt: zPick(base, 'seededAt', 'seeded_at') || zPick(v, 'parentBaseSeededAt', 'parent_base_seeded_at'),
      // READY on paper but no pickup files under parents/{parentId}/ in S3 → treat as not ready (Register again)
      filesMissing: zPick(v, 'parentBaseFilesMissing', 'parent_base_files_missing') === true,
      // last / in-flight parent resync (re-publish of every managed child after a direct S3 re-upload)
      resync: normResync(zPick(base, 'resync') || zPick(v, 'parentBaseResync', 'parent_base_resync')),
    },
    // no own S3 folder → the pricing Lambda is routed to the parent folder for this account
    inheritsParentFolder: zPick(v, 'inheritsParentFolder', 'inherits_parent_folder') === true,
    overrides: overridesRaw.map(normOverride).filter((o) => !o.deleted),
    // overrides in the last successful publish (pricing uses them); the rest are saved but NOT live
    // null = backend didn't send it (older backend) — callers fall back to the publish hash, NOT "nothing is live"
    liveOverrideIds: Array.isArray(liveIds) ? liveIds : null,
    // deleted since the last publish — pricing STILL uses these until the next publish
    pendingRemovals: (zPick(v, 'pendingRemovals', 'pending_removals') || []).map(normOverride),
    hasUnpublishedChanges: zPick(v, 'hasUnpublishedChanges', 'has_unpublished_changes') === true,
    // pickups a failed/interrupted publish left unconfirmed — the next publish rewrites them
    pendingPickupCount: Number(zPick(v, 'pendingPickupCount', 'pending_pickup_count')) || 0,
    activePublishJobId: zPick(v, 'activePublishJobId', 'active_publish_job_id'),
    parentBaseSeedJobId: zPick(v, 'parentBaseSeedJobId', 'parent_base_seed_job_id'),
    lastPublishedAt: zPick(setup, 'lastPublishedAt', 'last_published_at'),
    publishHistory: zPick(setup, 'publishHistory', 'publish_history') || [],
    raw: v,
  }
}

/** zone_base.resync sub-doc → {status, running, finishedAt, by, currentProzoId, pending[], done[], failed[], skipped[]}. */
export const normResync = (raw) => {
  if (!raw) return null
  const list = (...keys) => zPick(raw, ...keys) || []
  const status = String(zPick(raw, 'status') || '').toUpperCase()
  return {
    status,
    running: status === 'RUNNING',
    finishedAt: zPick(raw, 'finishedAt', 'finished_at'),
    by: zPick(raw, 'by'),
    currentProzoId: zPick(raw, 'currentProzoId', 'current_prozo_id'),
    pending: list('pendingProzoIds', 'pending_prozo_ids'),
    done: list('doneProzoIds', 'done_prozo_ids'),
    failed: list('failedProzoIds', 'failed_prozo_ids'),
    skipped: list('skippedProzoIds', 'skipped_prozo_ids'),
  }
}

const ZTERMINAL = ['COMPLETED', 'SUCCESS', 'SUCCEEDED', 'DONE', 'FAILED', 'FAILURE', 'ERROR', 'CANCELLED']

/** Job tracker doc → {pct, done, total, failed, status, terminal, failedJob}. */
export const normJob = (raw) => {
  const j = raw || {}
  // fileProgress is sometimes an object {total,processed,...} and sometimes a plain
  // status string ("IN_PROGRESS"/"COMPLETED") — handle both.
  const fpRaw = zPick(j, 'fileProgress', 'file_progress')
  const fp = fpRaw && typeof fpRaw === 'object' ? fpRaw : {}
  const fpStatus = typeof fpRaw === 'string' ? fpRaw : ''
  const es = zPick(j, 'executionStatus', 'execution_status') || {}
  const total = zNum(zPick(fp, 'total', 'totalFiles')) ?? zNum(zPick(es, 'total', 'totalTasks', 'totalCount'))
  const done =
    zNum(zPick(fp, 'processed', 'done', 'completed')) ??
    zNum(zPick(es, 'processed', 'completed', 'success', 'successCount')) ??
    0
  const failed = zNum(zPick(es, 'failed', 'failure', 'failedCount')) ?? zNum(zPick(fp, 'failed')) ?? 0
  const status = String(zPick(j, 'status', 'jobStatus') || zPick(es, 'status') || fpStatus || '').toUpperCase()
  const reachedTotal = total != null && total > 0 && done + failed >= total
  const terminal = ZTERMINAL.indexOf(status) >= 0 || reachedTotal
  const failedJob = ['FAILED', 'FAILURE', 'ERROR', 'CANCELLED'].indexOf(status) >= 0 || (terminal && failed > 0)
  const pct = total ? Math.min(100, Math.round(((done + failed) / total) * 100)) : terminal ? 100 : null
  return {pct, done, total, failed, status, terminal, failedJob, raw: j}
}
